import type { ExposureRegistryEntry } from '@akem/shared';
import React from 'react';

import { getExposureEntry, type ExposureHistoryEntry } from '../../../services/secondOrder.js';

interface ExposureState {
  exposure: ExposureRegistryEntry | null;
  history: ExposureHistoryEntry[];
  loading: boolean;
  error: string | null;
  empty: boolean;
}

interface LoadOptions {
  silent?: boolean;
}
interface UseExposureOptions {
  includeHistory?: boolean;
  historyLimit?: number;
}

export function useExposureEntry(
  instrumentId: string | null | undefined,
  options?: UseExposureOptions
) {
  const includeHistory = options?.includeHistory ?? false;
  const historyLimit = options?.historyLimit;
  const [state, setState] = React.useState<ExposureState>({
    exposure: null,
    history: [],
    loading: false,
    error: null,
    empty: false,
  });
  const requestRef = React.useRef(0);

  const load = React.useCallback(
    async (loadOptions?: LoadOptions) => {
      if (!instrumentId) {
        requestRef.current += 1;
        setState({ exposure: null, history: [], loading: false, error: null, empty: false });
        return;
      }

      const requestId = requestRef.current + 1;
      requestRef.current = requestId;

      if (!loadOptions?.silent) {
        setState((prev) => ({
          exposure: prev.exposure,
          history: prev.history,
          loading: true,
          error: null,
          empty: prev.empty,
        }));
      }

      try {
        const result = await getExposureEntry(instrumentId, {
          includeHistory,
          historyLimit,
        });
        if (requestRef.current !== requestId) return;
        setState({
          exposure: result?.exposure ?? null,
          history: result?.history ?? [],
          loading: false,
          error: null,
          empty: !result?.exposure,
        });
      } catch (err) {
        if (requestRef.current !== requestId) return;
        const message =
          err instanceof Error ? err.message : 'Failed to load exposure.';
        setState({
          exposure: null,
          history: [],
          loading: false,
          error: message,
          empty: false,
        });
      }
    },
    [instrumentId, historyLimit, includeHistory]
  );

  React.useEffect(() => {
    load({ silent: true });
  }, [load]);

  React.useEffect(() => {
    return () => {
      requestRef.current += 1;
    };
  }, []);

  return {
    exposure: state.exposure,
    history: state.history,
    loading: state.loading,
    error: state.error,
    empty: state.empty,
    refresh: () => load(),
  };
}
